import { call, fork, put, take, select } from 'redux-saga/effects';
import { isAuthorized } from '../utils/tokenUtils';

import {
    FETCH_FAVORITES,
    FAVORITE_STYLE,
    UNFAVORITE_STYLE,
    fetchFavorites,
    fetchFavoritesSuccess,
    fetchFavoritesError,
    favoriteStyleSuccess,
    favoriteStyleError,
    unfavoriteStyleSuccess,
    unfavoriteStyleError
} from '../actions/favoritesActions';
import { LOGIN_SUCCESS } from '../actions/userActions';

import { debug, error } from '../utils/logger';

import { makeRequest } from './makeRequest';

import { isFavoritesEnabled, createFavoriteLookupKeyForStyle } from '../utils/favoritesUtils';

import apiRoutes from '../utils/apiRoutes';
import localCache from '../utils/cacheUtils';
import { CACHE_KEY_GET_FAVORITED_STYLES } from '../utils/apiRoutes';
import { createGAEvent } from '@core/js/utils/analyticsUtils';
import { handleUtagEvent } from '@core/js/actions/initialActions';

function addStyleToFavoritedStyles(style, favoritedStyles) {
    const key = createFavoriteLookupKeyForStyle(style);
    favoritedStyles[key] = style;
}

export function updateCachedFavorites(state) {
    // update localCache with favorited styles changes
    const favoritedStylesFromStore = state.core.favoritedStyles;
    let newFavoritesAPICacheValue = [];

    for (const key in favoritedStylesFromStore) {
        newFavoritesAPICacheValue.push(favoritedStylesFromStore[key]);
    }

    localCache.update(CACHE_KEY_GET_FAVORITED_STYLES, newFavoritesAPICacheValue);
}

export function* fetchFavoritedStyles() {
    try {
        const state = yield select();
        const favoritedStyles = yield call(makeRequest, apiRoutes.getFavoritedStyles(state));
        const newFavoritedStyles = {};

        favoritedStyles.forEach((style) => {
            addStyleToFavoritedStyles(style, newFavoritedStyles);
        });

        yield put(fetchFavoritesSuccess(newFavoritedStyles));
    } catch (err) {
        error(`Unable to get favorited styles: ${err}`);

        if (err.status && err.status.code) {
            const errorServerResponse = err;

            if (404 === errorServerResponse.status.code) {
                yield put(fetchFavoritesSuccess({}));
            } else {
                yield put(fetchFavoritesError(errorServerResponse));
            }
        } else {
            yield put(fetchFavoritesError(err));
        }
    }

}

/**
 * Like a style. Call service to do the work
 * @param styleNumber Style number of the product being favorited
 * @param component Component that fired the originating action, optional.  Used to track
 *  actions from family products
 */
export function* addFavoritedStyle(styleNumber, displayName, component) {
    try {
        const state = yield select();
        yield call(makeRequest, apiRoutes.favoriteStyle(state, styleNumber));
        yield put(favoriteStyleSuccess(styleNumber, component));

        const gaEvent = createGAEvent(
            'PAGE_TYPE',
            'product heart added',
            displayName
        );
        yield put(handleUtagEvent(gaEvent, 'link'));
    } catch (errorServerResponse) {
        yield put(favoriteStyleError(errorServerResponse));
    }
}

/**
 * Unlike a style. Call service to do the work
 * @param styleNumber Style number of the product being unfavorited
 * @param component Component that fired the originating action, optional.  Used to track
 *  actions from family products
 */
export function* removeFavoritedStyle(styleNumber, displayName, component) {
    try {
        let state = yield select();
        yield call(makeRequest, apiRoutes.unfavoriteStyle(state, styleNumber));

        const styleToRemove = { styleNumber: styleNumber };
        const styleLookupKeyToRemove = createFavoriteLookupKeyForStyle(styleToRemove);
        yield put(unfavoriteStyleSuccess(styleLookupKeyToRemove, component));
        state = yield select();
        updateCachedFavorites(state);

        const gaEvent = createGAEvent(
            'PAGE_TYPE',
            'product heart removed',
            displayName
        );
        yield put(handleUtagEvent(gaEvent, 'link'));
    } catch (errorServerResponse) {
        yield put(unfavoriteStyleError(errorServerResponse));
    }
}

/**
 * Favorited styles requested
 *  * Fetch the styles
 *  * Dispatch update message to consumers of styles
 */
export function* fetchFavoritedStylesSaga() {
    debug("Saga: fetchFavoritedStylesSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Favorites not enabled. Exiting");
        return;
    }

    while (true) {
        yield take(FETCH_FAVORITES);

        // if user is not logged in, don't get the favorited styles (which would force a login)
        const authorized = yield call(isAuthorized);
        if (authorized) {
            // Only fetch the styles if they are authorized.  Otherwise they'll
            //  always be nagged with the login dialog
            yield call(fetchFavoritedStyles);
        } else {
            yield put(fetchFavoritesSuccess({}));
        }
    }
}

/**
 * User clicks the "Like", "Heart", "Add to Favorites"
 */
export function* favoriteStyleSaga() {
    debug("Saga: favoriteStyleSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Favorites not enabled. Exiting");
        return;
    }

    let action = undefined;
    while (action = yield take(FAVORITE_STYLE)) {
        if (action && action.payload) {
            const styleNumber = _.get(action, 'payload.styleNumber', '');
            const displayName = _.get(action, 'payload.displayName', '');
            const component = _.get(action, 'meta.component', {});

            yield fork(addFavoritedStyle, styleNumber, displayName, component);
        }
    }
}

/**
 * User clicks the "Unfavorite", "De-Heart", "Remove from Favorites"
 */
export function* unfavoriteStyleSaga() {
    debug("Saga: unfavoriteStyleSaga initialized");

    if (!isFavoritesEnabled()) {
        debug("Favorites not enabled. Exiting");
        return;
    }

    let action = undefined;
    while (action = yield take(UNFAVORITE_STYLE)) {
        if (action && action.payload) {
            const styleNumber = _.get(action, 'payload.styleNumber', '');
            const displayName = _.get(action, 'payload.displayName', '');
            const component = _.get(action, 'meta.component', {});

            yield fork(removeFavoritedStyle, styleNumber, displayName, component);
        }
    }
}

/**
 * Favorites should be fetched when a user successfully logs in
 */
export function *favoritesRefreshUserLoginSaga() {
    debug("Saga: favoritesRefreshUserLoginSaga initialized");

    while (true) {
        yield take(LOGIN_SUCCESS);
        yield put(fetchFavorites());
    }
}

export default [
    favoritesRefreshUserLoginSaga,
    fetchFavoritedStylesSaga,
    favoriteStyleSaga,
    unfavoriteStyleSaga
];
